import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { CompleteReminderRequest, Reminder } from "@/api/types";
import { localToday } from "@/hooks/useEquipment";
import { DialogAlert, EquipmentDialogShell } from "./EquipmentDialogShell";

const FIELD_OF: Record<string, "doneOn" | "hours" | "note"> = { doneon: "doneOn", hoursatdone: "hours", note: "note" };

/**
 * ef-061-8 — "Mark done: {title}": Done on (today), Hours at service (prefilled with the machine's current
 * hours, empty when it has no reading), Note. The server logs the service and RE-ARMS a repeating
 * reminder from this completion; a one-time one is retired. The page re-reads afterwards — whatever the
 * server now says is what shows. A repeat-by-hours reminder with no hours figure is a 400 on
 * `hoursAtDone`, shown inline with `aria-invalid` on Hours at service.
 */
export function CompleteReminderDialog({
  reminder,
  currentHours,
  onClose,
  onSaved,
}: {
  reminder: Reminder;
  /** The machine's current hours — what Hours at service starts as. */
  currentHours: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = localToday();
  const [doneOn, setDoneOn] = useState(today);
  const [hours, setHours] = useState(currentHours === null ? "" : String(currentHours));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const doneId = useId();
  const hoursId = useId();
  const noteId = useId();
  const inFlight = useRef(false);

  const flag = (f: string): true | undefined => (invalid.has(f) ? true : undefined);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return; // a double-click must not send a second request
    setError(null);
    setInvalid(new Set());

    const reading = hours.trim() === "" ? undefined : Number(hours);
    if (reading !== undefined && !(Number.isFinite(reading) && reading >= 0)) {
      setInvalid(new Set(["hours"]));
      setError("Hours at service: enter the meter reading, like 1250.5.");
      return;
    }
    if (doneOn === "") {
      setInvalid(new Set(["doneOn"]));
      setError("Done on: pick the day it was done.");
      return;
    }

    const body: CompleteReminderRequest = { doneOn };
    if (reading !== undefined) body.hoursAtDone = reading;
    if (note.trim() !== "") body.note = note.trim();

    inFlight.current = true;
    setSaving(true);
    try {
      await demoApi.completeReminder(reminder.id, body);
      onSaved();
      onClose();
    } catch (err) {
      const fields = Object.keys(problemFieldErrors(err) ?? {})
        .map((k) => FIELD_OF[k.toLowerCase()])
        .filter((f): f is "doneOn" | "hours" | "note" => f !== undefined);
      setInvalid(new Set(fields));
      setError(problemMessage(err, "Could not mark this done."));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const formId = useId();

  return (
    <EquipmentDialogShell
      title={`Mark done: ${reminder.title}`}
      saving={saving}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {({ alertId }) => (
        <form id={formId} className="emp-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className="equipment-dialog__row">
            <div className="emp-dialog__field">
              <label htmlFor={doneId}>Done on</label>
              <input
                id={doneId}
                type="date"
                max={today}
                value={doneOn}
                onChange={(e) => setDoneOn(e.target.value)}
                aria-invalid={flag("doneOn")}
                aria-describedby={invalid.has("doneOn") ? alertId : undefined}
              />
            </div>
            <div className="emp-dialog__field">
              <label htmlFor={hoursId}>Hours at service</label>
              <input
                id={hoursId}
                type="number"
                inputMode="decimal"
                step="any"
                min={0}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                aria-invalid={flag("hours")}
                aria-describedby={invalid.has("hours") ? alertId : undefined}
              />
            </div>
          </div>
          <div className="emp-dialog__field">
            <label htmlFor={noteId}>Note</label>
            <input
              id={noteId}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoComplete="off"
              aria-invalid={flag("note")}
              aria-describedby={invalid.has("note") ? alertId : undefined}
            />
          </div>

          <DialogAlert id={alertId} message={error} />
        </form>
      )}
    </EquipmentDialogShell>
  );
}
